import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../../context/AuthContext.jsx';
import { apiClient } from '../../../lib/apiClient.js';
import { formatCurrency } from '../../../lib/format.js';
import ActionHistoryPanel from '../../../components/ui/ActionHistoryPanel.jsx';

// ── Icons ──────────────────────────────────────────────────────────────────────

function ChatIcon() {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.6} strokeLinecap="round" strokeLinejoin="round" className="w-4 h-4">
      <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" />
    </svg>
  );
}

function DocumentIcon() {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.6} strokeLinecap="round" strokeLinejoin="round" className="w-4 h-4">
      <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
      <polyline points="14 2 14 8 20 8" />
      <line x1="16" y1="13" x2="8" y2="13" />
      <line x1="16" y1="17" x2="8" y2="17" />
      <polyline points="10 9 9 9 8 9" />
    </svg>
  );
}

function CheckCircleIcon() {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.6} strokeLinecap="round" strokeLinejoin="round" className="w-4 h-4">
      <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14" />
      <polyline points="22 4 12 14.01 9 11.01" />
    </svg>
  );
}

function BankIcon() {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.6} strokeLinecap="round" strokeLinejoin="round" className="w-4 h-4">
      <line x1="3" y1="22" x2="21" y2="22" />
      <line x1="6" y1="18" x2="6" y2="11" />
      <line x1="10" y1="18" x2="10" y2="11" />
      <line x1="14" y1="18" x2="14" y2="11" />
      <line x1="18" y1="18" x2="18" y2="11" />
      <polygon points="12 2 20 7 4 7" />
    </svg>
  );
}

function SendIcon() {
  return (
    <svg viewBox="0 0 24 24" className="w-4 h-4 fill-white">
      <path d="M2.01 21L23 12 2.01 3 2 10l15 2-15 2z" />
    </svg>
  );
}

// ── Process steps ──────────────────────────────────────────────────────────────

const PROCESS_STEPS = [
  { num: 1, icon: <ChatIcon />,         label: 'Submit Request' },
  { num: 2, icon: <DocumentIcon />,     label: 'Upload Document' },
  { num: 3, icon: <CheckCircleIcon />,  label: 'Admin Review' },
  { num: 4, icon: <BankIcon />,         label: 'Funds Released' },
];

// ── Qualifying expense types ───────────────────────────────────────────────────

const EXPENSE_TYPES = [
  {
    emoji: '🏥',
    title: 'Medical bills',
    description: 'Unreimbursed medical care for you, spouse, or dependents',
  },
  {
    emoji: '🏠',
    title: 'Eviction / Foreclosure',
    description: 'Prevention of eviction from or foreclosure on primary home',
  },
  {
    emoji: '🎓',
    title: 'Post-secondary tuition',
    description: 'Tuition, fees, and room & board for next 12 months',
  },
  {
    emoji: '⚰️',
    title: 'Funeral expenses',
    description: 'Funeral or burial expenses for family member',
  },
  {
    emoji: '🔥',
    title: 'Casualty loss',
    description: 'Repairs to primary residence from disaster or casualty',
  },
  {
    emoji: '🆘',
    title: 'Disaster relief',
    description: 'Federally-declared disaster area expenses',
  },
];

// ── Fields to mention ─────────────────────────────────────────────────────────

const FIELDS = [
  {
    label: 'Amount',
    hint: 'must not exceed the actual hardship need',
    required: true,
  },
  {
    label: 'Qualifying reason',
    hint: 'medical, eviction, tuition, funeral, casualty, or disaster',
    required: true,
  },
  {
    label: 'Supporting document',
    hint: 'uploaded after submission — the sponsor cannot approve without it',
    required: true,
  },
  {
    label: 'Tax acknowledgment',
    hint: 'you will be prompted to confirm this is a taxable event',
    required: true,
  },
];

// ── Example queries ────────────────────────────────────────────────────────────

const EXAMPLES = [
  'I need a hardship withdrawal of $5,000 for medical bills',
  'Hardship withdrawal for $6,000 — eviction prevention',
  'Request a hardship distribution of $8,000 for unpaid tuition',
  'I need $5,000 for funeral expenses for my father',
];

// ── Status banner config ───────────────────────────────────────────────────────

const STATUS_CONFIG = {
  pending_review: {
    borderColor: 'border-l-[#B45309]',
    bg: 'bg-[#FFFBEB]',
    textColor: 'text-[#92400E]',
    mutedColor: 'text-[#B45309]',
    label: 'Pending Review',
    message: 'Your hardship request is pending administrator review.',
  },
  approved: {
    borderColor: 'border-l-[#15803D]',
    bg: 'bg-[#F0FDF4]',
    textColor: 'text-[#14532D]',
    mutedColor: 'text-[#15803D]',
    label: 'Approved',
    message: 'Your hardship request has been approved.',
  },
  denied: {
    borderColor: 'border-l-[#DC2626]',
    bg: 'bg-[#FEF2F2]',
    textColor: 'text-[#7F1D1D]',
    mutedColor: 'text-[#DC2626]',
    label: 'Denied',
    message: 'Your hardship request was denied.',
  },
  awaiting_bank: {
    borderColor: 'border-l-[#B45309]',
    bg: 'bg-[#FFFBEB]',
    textColor: 'text-[#92400E]',
    mutedColor: 'text-[#B45309]',
    label: 'Awaiting Bank Details',
    message: 'Your request is approved — bank details required to release funds.',
  },
  executed: {
    borderColor: 'border-l-[#15803D]',
    bg: 'bg-[#F0FDF4]',
    textColor: 'text-[#14532D]',
    mutedColor: 'text-[#15803D]',
    label: 'Executed',
    message: 'Your hardship distribution has been processed.',
  },
};

function titleCaseExpense(str) {
  if (!str) return '';
  return str.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());
}

function formatTimestamp(ts) {
  if (!ts) return '';
  const d = new Date(ts);
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
}

// ── Status Banner ─────────────────────────────────────────────────────────────

function StatusBanner({ item }) {
  const cfg = STATUS_CONFIG[item.type] || STATUS_CONFIG['pending_review'];
  return (
    <div className={[
      'border-l-4 rounded-r-xl px-4 py-3.5',
      cfg.borderColor,
      cfg.bg,
    ].join(' ')}>
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <p className={['text-[13px] font-semibold', cfg.textColor].join(' ')}>
            {cfg.label}
            {item.expense_type && (
              <span className={['font-normal ml-1.5', cfg.mutedColor].join(' ')}>
                — {titleCaseExpense(item.expense_type)}
              </span>
            )}
          </p>
          <p className={['text-xs mt-0.5', cfg.mutedColor].join(' ')}>{cfg.message}</p>
        </div>
        <div className="text-right flex-shrink-0">
          {item.amount != null && (
            <p className={['text-[15px] font-bold tabular-nums', cfg.textColor].join(' ')}>
              {formatCurrency(item.amount)}
            </p>
          )}
          {item.timestamp && (
            <p className={['text-[10px] mt-0.5', cfg.mutedColor].join(' ')}>
              {formatTimestamp(item.timestamp)}
            </p>
          )}
        </div>
      </div>
    </div>
  );
}

// ── Process Flow ──────────────────────────────────────────────────────────────

function ProcessFlow() {
  return (
    <div className="card p-5">
      <h3 className="text-xs font-mono uppercase tracking-widest text-text-faint mb-5">
        How it works
      </h3>
      <div className="flex items-start justify-between relative">
        {/* Connector line sits behind the circles */}
        <div
          className="absolute top-4 left-0 right-0 h-px bg-border"
          style={{ zIndex: 0 }}
          aria-hidden="true"
        />
        {PROCESS_STEPS.map((step) => (
          <div key={step.num} className="flex flex-col items-center flex-1 relative" style={{ zIndex: 1 }}>
            {/* Number circle + icon */}
            <div className="w-8 h-8 rounded-full bg-bg border-2 border-border flex items-center justify-center text-text-muted">
              {step.icon}
            </div>
            {/* Step number badge */}
            <div className="absolute -top-1 -right-0.5 w-4 h-4 rounded-full bg-accent-light border border-accent/30 flex items-center justify-center">
              <span className="text-[9px] font-bold text-accent-dark">{step.num}</span>
            </div>
            {/* Label */}
            <p className="text-[11px] text-text-muted text-center mt-2.5 leading-tight px-1">
              {step.label}
            </p>
          </div>
        ))}
      </div>
    </div>
  );
}

// ── Main component ────────────────────────────────────────────────────────────

export default function HardshipPage() {
  const navigate = useNavigate();
  const { principal } = useAuth();
  const [isLoading, setIsLoading] = useState(true);
  const [hardshipItems, setHardshipItems] = useState([]);
  const [selectedQuery, setSelectedQuery] = useState('');
  const [blackoutActive, setBlackoutActive] = useState(false);

  useEffect(() => {
    let cancelled = false;
    async function load() {
      setIsLoading(true);
      try {
        const [res, participantsRes] = await Promise.all([
          apiClient.getParticipantActivity(),
          apiClient.listParticipants().catch(() => ({ participants: [] })),
        ]);
        if (cancelled) return;
        const items = (res.activities || []).filter(
          (a) => a.action === 'hardship_distribution'
        );
        setHardshipItems(items);
        const me = (participantsRes?.participants ?? []).find(
          (p) => p.participant_id === principal?.participantId
        );
        setBlackoutActive(me?.blackout_active ?? false);
      } catch {
        if (!cancelled) setHardshipItems([]);
      } finally {
        if (!cancelled) setIsLoading(false);
      }
    }
    load();
    return () => { cancelled = true; };
  }, [principal?.participantId]);

  function launchChat(query) {
    if (!query.trim()) return;
    navigate('/participant/chat', { state: { chatDraft: query.trim() } });
  }

  // Only show banners for active (non-executed, non-cancelled) records, plus executed/denied
  const activeBannerItems = hardshipItems.filter((a) =>
    ['pending_review', 'awaiting_bank', 'approved', 'denied', 'executed'].includes(a.type)
  );

  return (
    <div className="max-w-2xl mx-auto space-y-5">

      {/* Hero card */}
      <div className="card overflow-hidden flex">
        <div className="w-1 bg-accent flex-shrink-0" />
        <div className="p-5 flex-1">
          <h1 className="text-[20px] font-bold text-text leading-tight">Hardship Withdrawal</h1>
          <p className="text-sm text-text-muted mt-1 leading-relaxed">
            Access funds early for an immediate and heavy financial need under IRC §401(k)(2)(B)(i)(IV).
            Six qualifying expense types are permitted. Every request requires a supporting document
            and plan administrator approval before funds are released.
          </p>
        </div>
      </div>

      {/* Blackout banner */}
      {!isLoading && blackoutActive && (
        <div className="card p-4 border-warning/40 bg-warning/5 flex items-start gap-3">
          <div className="w-8 h-8 rounded-full bg-warning/15 text-warning flex items-center justify-center text-sm font-bold flex-shrink-0">!</div>
          <div>
            <p className="text-[13px] font-semibold text-warning">Plan blackout in effect</p>
            <p className="text-[12px] text-text-muted mt-0.5">
              All account writes are temporarily suspended per ERISA §101(i). Hardship requests cannot be submitted until the blackout ends.
            </p>
          </div>
        </div>
      )}

      {/* ── Status banners ── */}
      {!isLoading && activeBannerItems.length > 0 && (
        <div className="space-y-2">
          {activeBannerItems.map((item) => (
            <StatusBanner key={item.entry_id || item.id || item.timestamp} item={item} />
          ))}
        </div>
      )}

      {/* ── Loading skeleton for banners ── */}
      {isLoading && (
        <div className="h-14 rounded-xl bg-bg-s2 border border-border animate-pulse" />
      )}

      {/* ── Process flow ── */}
      <ProcessFlow />

      {/* ── Qualifying expenses grid ── */}
      <div className="card p-5">
        <h3 className="text-xs font-mono uppercase tracking-widest text-text-faint mb-4">
          Qualifying expense types
        </h3>
        <div className="grid grid-cols-2 gap-3">
          {EXPENSE_TYPES.map((exp) => (
            <div
              key={exp.title}
              className="border border-border rounded-xl px-3.5 py-3 bg-bg-s2 hover:bg-bg-s3 transition-colors duration-150"
            >
              <div className="flex items-start gap-2.5">
                <span className="text-xl leading-none flex-shrink-0 mt-0.5" role="img" aria-label={exp.title}>
                  {exp.emoji}
                </span>
                <div>
                  <p className="text-[13px] font-semibold text-text leading-snug">{exp.title}</p>
                  <p className="text-[11px] text-text-muted mt-0.5 leading-relaxed">{exp.description}</p>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* ── Fields to mention ── */}
      <div className="card p-5">
        <h3 className="text-xs font-mono uppercase tracking-widest text-text-faint mb-4">
          What to include in your request
        </h3>
        <ul className="space-y-3">
          {FIELDS.map((f) => (
            <li key={f.label} className="flex items-start gap-3">
              <div className="w-5 h-5 rounded-full bg-accent-light flex items-center justify-center flex-shrink-0 mt-0.5">
                <span className="text-[9px] font-bold text-accent-dark">!</span>
              </div>
              <div>
                <span className="text-sm font-medium text-text">{f.label}</span>
                {f.hint && (
                  <span className="text-xs text-text-muted ml-1.5">— {f.hint}</span>
                )}
              </div>
            </li>
          ))}
        </ul>
      </div>

      {/* ── Example queries ── */}
      <div className="card p-5 space-y-4">
        <h3 className="text-xs font-mono uppercase tracking-widest text-text-faint">
          Example queries — click to use
        </h3>

        <div className="space-y-2">
          {EXAMPLES.map((ex) => (
            <button
              key={ex}
              type="button"
              onClick={() => setSelectedQuery(ex)}
              className={[
                'w-full text-left text-sm px-4 py-3 rounded-lg border transition-colors duration-150',
                selectedQuery === ex
                  ? 'border-accent bg-accent-light text-accent-dark font-medium'
                  : 'border-border text-text-muted hover:border-accent/40 hover:bg-bg-s2 hover:text-text',
              ].join(' ')}
            >
              <span className="font-mono text-accent opacity-60 mr-1">"</span>
              {ex}
              <span className="font-mono text-accent opacity-60 ml-0.5">"</span>
            </button>
          ))}
        </div>

        {/* Custom query */}
        <div>
          <h4 className="text-xs font-mono uppercase tracking-widest text-text-faint mb-2">
            Or write your own
          </h4>
          <textarea
            rows={2}
            value={selectedQuery}
            onChange={(e) => setSelectedQuery(e.target.value)}
            placeholder='e.g. "I need a hardship withdrawal of $5,000 for medical bills"'
            className="w-full border border-border-strong rounded-xl px-4 py-3 text-sm text-text bg-bg
              outline-none resize-none focus:border-accent focus:ring-2 focus:ring-accent/10
              placeholder:text-text-faint leading-relaxed transition-all duration-150"
          />
          <button
            type="button"
            onClick={() => launchChat(selectedQuery)}
            disabled={!selectedQuery.trim() || blackoutActive}
            title={blackoutActive ? 'Plan blackout in effect — requests disabled' : undefined}
            className="mt-2 w-full btn-primary flex items-center justify-center gap-2"
          >
            <SendIcon />
            {blackoutActive ? 'Blackout in effect' : 'Send to chat'}
          </button>
        </div>

        {/* Tax warning callout */}
        <div className="rounded-xl border border-[#FDE68A] bg-[#FFFBEB] px-4 py-3">
          <p className="text-xs text-[#92400E] leading-relaxed">
            <span className="font-semibold">⚠️ Tax notice —</span>{' '}
            Hardship distributions are subject to ordinary income tax and a 10% early withdrawal
            penalty under IRC §72(t) for participants under age 59½. They cannot be repaid.
          </p>
        </div>
      </div>

      <ActionHistoryPanel
        title="Request History"
        items={hardshipItems}
        isLoading={isLoading}
        renderItem={(item) => {
          const STATUS_LABELS = {
            executed:      'Executed',
            pending_review:'Pending Review',
            approved:      'Approved',
            denied:        'Denied',
            awaiting_bank: 'Awaiting Bank Details',
            cancelled:     'Cancelled',
          };
          const STATUS_COLORS = {
            executed:      'text-success',
            pending_review:'text-warning',
            approved:      'text-success',
            denied:        'text-danger',
            awaiting_bank: 'text-warning',
            cancelled:     'text-text-faint',
          };
          return (
            <div className="flex items-center gap-3 text-xs">
              {item.amount != null && (
                <span className="text-text font-semibold font-mono">{formatCurrency(item.amount)}</span>
              )}
              <span className={STATUS_COLORS[item.type] || 'text-text-muted'}>
                {STATUS_LABELS[item.type] || item.type}
              </span>
              {item.expense_type && (
                <span className="text-text-faint">— {titleCaseExpense(item.expense_type)}</span>
              )}
            </div>
          );
        }}
      />

    </div>
  );
}
